import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';
import { FiEdit, FiPieChart, FiPlus, FiTrash2 } from 'react-icons/fi';

import BasePage from '../../components/BasePage';
import DataTable, { Column, FilterField } from '../../components/DataTable';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';

import AddResearchModal from './AddResearchModal';
import DeleteResearchModal from './DeleteResearchModal';

import { StatusBadge, IconButton, CreateButton } from './styles';

interface IResearch {
  id: string;
  title: string;
  status: string;
  description: string;
  start_date: Date;
  finish_date: Date;
}

interface ISyndicate {
  id: string;
  nome_fantasia: string;
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Rascunho' },
  { value: 'published', label: 'Publicado' },
  { value: 'finished', label: 'Finalizado' },
];

const STATUS_TONE: Record<string, 'success' | 'warning' | 'neutral'> = {
  published: 'success',
  finished: 'neutral',
  draft: 'warning',
};
const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  finished: 'Finalizado',
};

const Research: React.FC = () => {
  const history = useHistory();
  const { user, syndicate } = useAuth();
  const isSyndicateUser = user?.role === 'syndicate';

  const [loading, setLoading] = useState(true);
  const [researches, setResearches] = useState<IResearch[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterParams, setFilterParams] = useState<Record<string, string>>(
    isSyndicateUser && syndicate?.id ? { syndicate_id: syndicate.id } : {},
  );
  const [unions, setUnions] = useState<ISyndicate[]>([]);

  const [addShowModal, setAddShowModal] = useState(false);
  const [deleteShowModal, setDeleteShowModal] = useState(false);
  const [selectedResearch, setSelectedResearch] = useState('');

  useEffect(() => {
    document.title = 'Pesquisa - Viva Voz - Rede Suco de Laranja';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get<IResearch[]>('/research', {
          params: filterParams,
        });
        if (cancelled) return;
        // reverse so newest first, as before
        setResearches(response.data.slice().reverse());
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [filterParams]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const response = await api.get<ISyndicate[]>('/unions');
      if (mounted) setUnions(response.data);
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const filters = useMemo<FilterField[]>(() => {
    const base: FilterField[] = [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        placeholder: 'Todos',
        options: STATUS_OPTIONS,
        span: isSyndicateUser ? 12 : 4,
      },
    ];
    if (!isSyndicateUser) {
      base.push({
        key: 'syndicate_id',
        label: 'Sindicato',
        type: 'select',
        placeholder: 'Todos',
        options: unions.map(u => ({ value: u.id, label: u.nome_fantasia })),
        span: 8,
      });
    }
    return base;
  }, [unions, isSyndicateUser]);

  const columns = useMemo<Column<IResearch>[]>(
    () => [
      {
        key: 'title',
        label: 'Título',
        width: '22%',
        render: r => (
          <span style={{ fontWeight: 600, color: '#0f172a' }}>{r.title}</span>
        ),
      },
      {
        key: 'description',
        label: 'Descrição',
        render: r => (
          <span
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: '#475569',
            }}
            title={r.description}
          >
            {r.description}
          </span>
        ),
      },
      {
        key: 'start_date',
        label: 'Início',
        width: '14%',
        render: r => format(new Date(r.start_date), "dd/MM/yyyy 'às' HH:mm"),
      },
      {
        key: 'finish_date',
        label: 'Fim',
        width: '14%',
        render: r => format(new Date(r.finish_date), "dd/MM/yyyy 'às' HH:mm"),
      },
      {
        key: 'status',
        label: 'Status',
        width: '11%',
        render: r => (
          <StatusBadge $tone={STATUS_TONE[r.status] || 'neutral'}>
            {STATUS_LABEL[r.status] || r.status}
          </StatusBadge>
        ),
      },
    ],
    [],
  );

  const handleFiltersChange = useCallback(
    (values: Record<string, string>) => {
      const next = { ...values };
      if (isSyndicateUser && syndicate?.id) {
        next.syndicate_id = syndicate.id;
      }
      setFilterParams(next);
      setCurrentPage(1);
    },
    [isSyndicateUser, syndicate],
  );

  return (
    <BasePage title="Pesquisas">
      <Container fluid style={{ padding: 0 }}>
        <DataTable<IResearch>
          title="Pesquisas"
          subtitle="Crie, publique e acompanhe pesquisas enviadas aos associados"
          columns={columns}
          data={researches}
          rowKey="id"
          loading={loading}
          page={currentPage}
          pageSize={researches.length || 1}
          total={researches.length}
          onPageChange={setCurrentPage}
          filters={filters}
          filterValues={filterParams}
          onFiltersChange={handleFiltersChange}
          headerActions={(
            <CreateButton type="button" onClick={() => setAddShowModal(true)}>
              <FiPlus size={14} />
              Nova pesquisa
            </CreateButton>
          )}
          rowActions={row => (
            <>
              <IconButton
                type="button"
                title="Resultados"
                disabled={row.status === 'draft'}
                onClick={() => history.push(`/research/results/${row.id}`)}
              >
                <FiPieChart size={14} />
              </IconButton>
              {row.status !== 'finished' && (
                <IconButton
                  type="button"
                  title="Editar"
                  onClick={() => history.push(`/research/${row.id}`)}
                >
                  <FiEdit size={14} />
                </IconButton>
              )}
              <IconButton
                type="button"
                title="Remover"
                $danger
                onClick={() => {
                  setSelectedResearch(row.id);
                  setDeleteShowModal(true);
                }}
              >
                <FiTrash2 size={14} />
              </IconButton>
            </>
          )}
          emptyMessage="Nenhuma pesquisa encontrada."
        />

        <AddResearchModal
          handleShow={setAddShowModal}
          show={addShowModal}
          handleResearches={setResearches}
          handleLoading={setLoading}
        />

        <DeleteResearchModal
          handleShow={setDeleteShowModal}
          show={deleteShowModal}
          handleLoading={setLoading}
          syndicate={filterParams.syndicate_id || ''}
          handleResearches={setResearches}
          selectedResearch={selectedResearch}
        />
      </Container>
    </BasePage>
  );
};

export default Research;
